import {FC, useCallback, useEffect, useState} from 'react'
import {
  getPaymentBreakdown,
  getInstallments,
  getInstallmentSchedule,
  getPayoutForecast,
  MonthBreakdown,
  InstallmentsResponse,
  ScheduleResponse,
  PayoutForecastResponse,
} from './_requests'

const nis = (n: number) => '₪' + Math.round(n).toLocaleString('he-IL')

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return `${HE_MONTHS[m - 1]} ${y}`
}

// previous calendar month as YYYY-MM
const prevMonthKey = () => {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
}

const card = (bg: string): React.CSSProperties => ({
  background: bg,
  borderRadius: 12,
  padding: '18px 20px',
  color: '#fff',
  flex: '1 1 160px',
  minWidth: 160,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
})

const PaymentBreakdownPage: FC = () => {
  const [months, setMonths] = useState<MonthBreakdown[]>([])
  const [duplicatesDropped, setDuplicatesDropped] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedMonth, setSelectedMonth] = useState<string>(prevMonthKey())

  const [inst, setInst] = useState<InstallmentsResponse | null>(null)
  const [instLoading, setInstLoading] = useState(false)
  const [instError, setInstError] = useState('')

  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null)
  const [payout, setPayout] = useState<PayoutForecastResponse | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      // Show the current year, month by month.
      const from = new Date().getFullYear() + '-01-01'
      const {data} = await getPaymentBreakdown({from})
      setMonths(data.months || [])
      setDuplicatesDropped(data.duplicates_dropped || 0)
      // default selected = previous month if present, else last available
      const keys = (data.months || []).map((m) => m.month)
      if (keys.length && !keys.includes(selectedMonth)) {
        setSelectedMonth(keys[keys.length - 1])
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בטעינת הנתונים')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadInstallments = useCallback(async (month: string) => {
    setInstLoading(true)
    setInstError('')
    setInst(null)
    try {
      const {data} = await getInstallments(month)
      setInst(data)
    } catch (e: any) {
      setInstError(e?.response?.data?.error || e.message || 'שגיאה בטעינת תשלומים')
    } finally {
      setInstLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    getInstallmentSchedule().then(({data}) => setSchedule(data)).catch(() => setSchedule(null))
    getPayoutForecast().then(({data}) => setPayout(data)).catch(() => setPayout(null))
  }, [load])

  useEffect(() => {
    if (selectedMonth) loadInstallments(selectedMonth)
  }, [selectedMonth, loadInstallments])

  const focus = months.find((m) => m.month === selectedMonth)

  return (
    <div style={{direction: 'rtl', padding: '24px 28px', maxWidth: 1200, margin: '0 auto', fontFamily: 'inherit'}}>
      <div style={{marginBottom: 6}}>
        <h1 style={{fontSize: 26, fontWeight: 700, margin: 0}}>פירוט תשלומים לפי חודש</h1>
        <div style={{color: '#6b7280', marginTop: 4, fontSize: 14}}>
          כרטיסי אשראי (Cardcom / Tranzila) מול העברות בנקאיות, צ׳קים וזיכויים. מקור: חשבוניות Cardcom (לאחר ניקוי כפילויות).
        </div>
      </div>

      {duplicatesDropped > 0 && (
        <div style={{background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 12}}>
          נוקו {duplicatesDropped.toLocaleString('he-IL')} חשבוניות כפולות (באג שמירה בו כל חשבונית נשמרה פעמיים) — הסכומים למטה נקיים.
        </div>
      )}

      {loading ? (
        <div style={{padding: 60, textAlign: 'center'}}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : error ? (
        <div style={{background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: 16, marginTop: 16}}>{error}</div>
      ) : (
        <>
          {/* Focus month hero */}
          {focus && (
            <div style={{marginTop: 18}}>
              <div style={{fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 10}}>
                {monthLabel(focus.month)} · סה״כ נטו {nis(focus.net)}
              </div>
              {/* Highlighted: what the credit-card company should deposit */}
              <div style={{...card('linear-gradient(135deg,#111827,#374151)'), flexBasis: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <div style={{fontSize: 14, opacity: 0.85}}>צריך להתקבל מחברת האשראי</div>
                  <div style={{fontSize: 32, fontWeight: 800, marginTop: 2}}>{nis(focus.card_settlement)}</div>
                  <div style={{fontSize: 12, opacity: 0.75, marginTop: 4}}>
                    Cardcom {nis(focus.cardcom.amount)} + Tranzila {nis(focus.tranzila.amount)} − זיכויי אשראי {nis(focus.card_refund.amount)}
                  </div>
                </div>
                <div style={{fontSize: 12, opacity: 0.7, maxWidth: 240, textAlign: 'left'}}>
                  אשראי בלבד (ללא העברות בנקאיות / צ׳קים). עסקאות בתשלומים עשויות להיזקף על פני מספר חודשים לפי הסכם הסליקה.
                </div>
              </div>

              <div style={{display: 'flex', flexWrap: 'wrap', gap: 12}}>
                <div style={card('linear-gradient(135deg,#4f46e5,#7c3aed)')}>
                  <div style={{fontSize: 13, opacity: 0.85}}>כרטיס Cardcom</div>
                  <div style={{fontSize: 24, fontWeight: 700}}>{nis(focus.cardcom.amount)}</div>
                  <div style={{fontSize: 12, opacity: 0.85}}>{focus.cardcom.count} עסקאות</div>
                </div>
                <div style={card('linear-gradient(135deg,#0ea5e9,#2563eb)')}>
                  <div style={{fontSize: 13, opacity: 0.85}}>כרטיס Tranzila</div>
                  <div style={{fontSize: 24, fontWeight: 700}}>{nis(focus.tranzila.amount)}</div>
                  <div style={{fontSize: 12, opacity: 0.85}}>{focus.tranzila.count} עסקאות</div>
                </div>
                <div style={card('linear-gradient(135deg,#059669,#10b981)')}>
                  <div style={{fontSize: 13, opacity: 0.85}}>העברה בנקאית</div>
                  <div style={{fontSize: 24, fontWeight: 700}}>{nis(focus.bank_transfer.amount)}</div>
                  <div style={{fontSize: 12, opacity: 0.85}}>{focus.bank_transfer.count} העברות</div>
                </div>
                {focus.cheque.count > 0 && (
                  <div style={card('linear-gradient(135deg,#d97706,#f59e0b)')}>
                    <div style={{fontSize: 13, opacity: 0.85}}>צ׳קים</div>
                    <div style={{fontSize: 24, fontWeight: 700}}>{nis(focus.cheque.amount)}</div>
                    <div style={{fontSize: 12, opacity: 0.85}}>{focus.cheque.count} צ׳קים</div>
                  </div>
                )}
                <div style={card('linear-gradient(135deg,#dc2626,#ef4444)')}>
                  <div style={{fontSize: 13, opacity: 0.85}}>זיכויים / החזרים</div>
                  <div style={{fontSize: 24, fontWeight: 700}}>-{nis(focus.refund.amount)}</div>
                  <div style={{fontSize: 12, opacity: 0.85}}>{focus.refund.count} זיכויים</div>
                </div>
              </div>
            </div>
          )}

          {/* Monthly table */}
          <div style={{marginTop: 28, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden'}}>
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 760}}>
                <thead>
                  <tr style={{background: '#f9fafb', textAlign: 'right', color: '#6b7280'}}>
                    <th style={th}>חודש</th>
                    <th style={th}>Cardcom</th>
                    <th style={th}>Tranzila</th>
                    <th style={{...th, color: '#111827'}}>מחברת האשראי</th>
                    <th style={th}>העברה בנקאית</th>
                    <th style={th}>צ׳קים</th>
                    <th style={th}>זיכויים</th>
                    <th style={{...th, fontWeight: 700, color: '#111827'}}>סה״כ נטו</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const sel = m.month === selectedMonth
                    return (
                      <tr
                        key={m.month}
                        onClick={() => setSelectedMonth(m.month)}
                        style={{cursor: 'pointer', background: sel ? '#eef2ff' : '#fff', borderTop: '1px solid #f3f4f6'}}
                      >
                        <td style={{...td, fontWeight: 600}}>{monthLabel(m.month)}</td>
                        <td style={td}>{nis(m.cardcom.amount)} <span style={countStyle}>({m.cardcom.count})</span></td>
                        <td style={td}>{m.tranzila.count ? <>{nis(m.tranzila.amount)} <span style={countStyle}>({m.tranzila.count})</span></> : <span style={{color: '#d1d5db'}}>—</span>}</td>
                        <td style={{...td, fontWeight: 600, color: '#111827'}}>{nis(m.card_settlement)}</td>
                        <td style={td}>{m.bank_transfer.count ? <>{nis(m.bank_transfer.amount)} <span style={countStyle}>({m.bank_transfer.count})</span></> : <span style={{color: '#d1d5db'}}>—</span>}</td>
                        <td style={td}>{m.cheque.count ? <>{nis(m.cheque.amount)} <span style={countStyle}>({m.cheque.count})</span></> : <span style={{color: '#d1d5db'}}>—</span>}</td>
                        <td style={{...td, color: m.refund.amount ? '#dc2626' : '#d1d5db'}}>{m.refund.amount ? '-' + nis(m.refund.amount) : '—'}</td>
                        <td style={{...td, fontWeight: 700}}>{nis(m.net)}</td>
                      </tr>
                    )
                  })}
                  {months.length === 0 && (
                    <tr><td colSpan={8} style={{...td, textAlign: 'center', color: '#9ca3af'}}>אין נתונים</td></tr>
                  )}
                </tbody>
                {months.length > 0 && (
                  <tfoot>
                    <tr style={{background: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: 700}}>
                      <td style={td}>סה״כ</td>
                      <td style={td}>{nis(sum(months, (m) => m.cardcom.amount))}</td>
                      <td style={td}>{nis(sum(months, (m) => m.tranzila.amount))}</td>
                      <td style={{...td, color: '#111827'}}>{nis(sum(months, (m) => m.card_settlement))}</td>
                      <td style={td}>{nis(sum(months, (m) => m.bank_transfer.amount))}</td>
                      <td style={td}>{nis(sum(months, (m) => m.cheque.amount))}</td>
                      <td style={{...td, color: '#dc2626'}}>-{nis(sum(months, (m) => m.refund.amount))}</td>
                      <td style={td}>{nis(sum(months, (m) => m.net))}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div style={{padding: '8px 14px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6'}}>
              לחצו על שורה כדי לראות פירוט תשלומים (installments) לאותו חודש.
            </div>
          </div>

          {/* Installments panel */}
          <div style={{marginTop: 24, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20}}>
            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 4}}>
              תשלומים (פריסה לתשלומים) · {selectedMonth ? monthLabel(selectedMonth) : ''}
            </div>
            {instLoading ? (
              <div style={{padding: 20}}><div className="spinner-border spinner-border-sm text-primary" role="status" /></div>
            ) : instError ? (
              <div style={{color: '#991b1b'}}>{instError}</div>
            ) : inst ? (
              <>
                <div style={{background: '#eff6ff', color: '#1e40af', borderRadius: 8, padding: '8px 12px', fontSize: 12, margin: '8px 0 14px'}}>
                  {inst.coverage_note}
                </div>
                <div style={{display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14}}>
                  <div>
                    <div style={{fontSize: 13, color: '#6b7280'}}>עסקאות בתשלומים (≥2)</div>
                    <div style={{fontSize: 22, fontWeight: 700}}>{inst.installment_deals}</div>
                  </div>
                  <div>
                    <div style={{fontSize: 13, color: '#6b7280'}}>סכום בתשלומים</div>
                    <div style={{fontSize: 22, fontWeight: 700}}>{nis(inst.installment_amount)}</div>
                  </div>
                  <div>
                    <div style={{fontSize: 13, color: '#6b7280'}}>עסקאות שנראו ב-API</div>
                    <div style={{fontSize: 22, fontWeight: 700}}>{inst.api_transactions_seen}</div>
                  </div>
                </div>
                <table style={{borderCollapse: 'collapse', fontSize: 14}}>
                  <thead>
                    <tr style={{color: '#6b7280', textAlign: 'right'}}>
                      <th style={th}>מס׳ תשלומים</th>
                      <th style={th}>כמות עסקאות</th>
                      <th style={th}>סכום</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inst.plans.map((p) => (
                      <tr key={p.payments} style={{borderTop: '1px solid #f3f4f6', background: p.payments > 1 ? '#fffbeb' : '#fff'}}>
                        <td style={td}>{p.payments === 1 ? 'תשלום אחד' : `${p.payments} תשלומים`}</td>
                        <td style={td}>{p.count}</td>
                        <td style={td}>{nis(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </div>

          {/* Installment payout schedule: what you should receive each month
              from installment (תשלומים) deals already charged. */}
          {schedule && schedule.schedule.length > 0 && (
            <div style={{marginTop: 24, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20}}>
              <div style={{fontSize: 16, fontWeight: 700, marginBottom: 4}}>
                לוח גבייה של תשלומים לפי חודש
              </div>
              <div style={{background: '#eff6ff', color: '#1e40af', borderRadius: 8, padding: '8px 12px', fontSize: 12, margin: '8px 0 14px'}}>
                {schedule.note}
              </div>
              <div style={{overflowX: 'auto'}}>
                <table style={{borderCollapse: 'collapse', fontSize: 14, minWidth: 560}}>
                  <thead>
                    <tr style={{color: '#6b7280', textAlign: 'right'}}>
                      <th style={th}>חודש גבייה</th>
                      <th style={th}>מס׳ תשלומים</th>
                      <th style={th}>סכום צפוי</th>
                      <th style={th}>מתוכם מחודשים קודמים</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.schedule.map((s) => {
                      const future = s.month > schedule.current_month
                      const current = s.month === schedule.current_month
                      return (
                        <tr key={s.month} style={{borderTop: '1px solid #f3f4f6', background: current ? '#eef2ff' : future ? '#f0fdf4' : '#fff'}}>
                          <td style={{...td, fontWeight: 600}}>
                            {monthLabel(s.month)}
                            {current && <span style={{marginRight: 6, fontSize: 11, color: '#4f46e5'}}>(החודש)</span>}
                            {future && <span style={{marginRight: 6, fontSize: 11, color: '#059669'}}>(צפוי)</span>}
                          </td>
                          <td style={td}>{s.payments}</td>
                          <td style={{...td, fontWeight: 600}}>{nis(s.amount)}</td>
                          <td style={{...td, color: '#6b7280'}}>
                            {s.carryover_payments ? <>{s.carryover_payments} · {nis(s.carryover_amount)}</> : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Forecast of acquirer (MAX) deposits by date, from Cardcom data.
              Installments are exact; the regular column is a model estimate
              (its exact date/cycle is issuer-dependent, only in the MAX report). */}
          {payout && payout.forecast.length > 0 && (
            <div style={{marginTop: 24, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8}}>
                <div style={{fontSize: 16, fontWeight: 700}}>צפי תקבולים מחברת האשראי לפי תאריך</div>
                <div style={{fontSize: 14}}>
                  תשלומים שטרם התקבלו: <b>{nis(payout.forecast.filter((p) => !p.paid).reduce((s, p) => s + p.installments, 0))}</b>
                  <span style={{fontSize: 12, color: '#6b7280'}}> (נטו, עמלה ~{(payout.fee_rate * 100).toFixed(2)}%)</span>
                </div>
              </div>
              <div style={{background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12, margin: '8px 0 14px'}}>
                עמודת <b>תשלומים</b> מדויקת. עמודת <b>רגילות</b> היא הערכה: המודל מזכה את כל עסקאות החודש ב-2 לחודש, אך MAX מזכה בשני מועדים (ה-2 וה-8) וחלק מהעסקאות מסולק תוך ימים — לכן הסכום בפועל ליום בודד קטן יותר (למשל 02/07 בפועל ~59k ולא הסכום החודשי המלא). Isracard/Amex ו-Tranzila מסולקים בנפרד.
              </div>
              <div style={{overflowX: 'auto'}}>
                <table style={{borderCollapse: 'collapse', fontSize: 14, minWidth: 620}}>
                  <thead>
                    <tr style={{color: '#6b7280', textAlign: 'right'}}>
                      <th style={th}>תאריך זיכוי</th>
                      <th style={th}>רגילות (הערכה)</th>
                      <th style={{...th, color: '#111827'}}>תשלומים</th>
                      <th style={th}>סה״כ נטו</th>
                      <th style={th}># תשלומים</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payout.forecast.map((p) => (
                      <tr key={p.date} style={{borderTop: '1px solid #f3f4f6', background: p.paid ? '#f9fafb' : '#f0fdf4'}}>
                        <td style={{...td, fontWeight: 600}}>
                          {p.date.split('-').reverse().join('/')}
                          <span style={{marginRight: 6, fontSize: 11, color: p.paid ? '#9ca3af' : '#059669'}}>
                            {p.paid ? '(שולם)' : '(צפוי)'}
                          </span>
                        </td>
                        <td style={{...td, color: '#9ca3af'}}>{p.regular ? '~' + nis(p.regular) : '—'}</td>
                        <td style={{...td, fontWeight: 600}}>{p.installments ? nis(p.installments) : '—'}</td>
                        <td style={td}>{nis(p.total)}</td>
                        <td style={{...td, color: '#6b7280'}}>{p.payments}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const th: React.CSSProperties = {padding: '10px 14px', fontWeight: 600, whiteSpace: 'nowrap'}
const td: React.CSSProperties = {padding: '10px 14px', whiteSpace: 'nowrap'}
const countStyle: React.CSSProperties = {color: '#9ca3af', fontSize: 12}
const sum = (arr: MonthBreakdown[], f: (m: MonthBreakdown) => number) => arr.reduce((s, m) => s + f(m), 0)

export default PaymentBreakdownPage
